import React, { useState, useEffect, useCallback } from "react";
import {
  Button, Tag, Typography, Progress, Empty, Spin, Popconfirm, message, Row, Col, Select, Tooltip, Checkbox,
} from "antd";
import {
  PlusOutlined, CalendarOutlined, TeamOutlined, ClockCircleOutlined,
  EditOutlined, DeleteOutlined, DollarOutlined, LinkOutlined, UserOutlined, CoffeeOutlined,
  InboxOutlined, CopyOutlined, LockOutlined, PrinterOutlined, FilePdfOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type { VenueEvent } from "../../../../utils/api/venueEventsAPI";
import {
  getVenueEvents, deleteVenueEvent, createVenueEvent, updateVenueEvent,
  archiveVenueEvent, duplicateVenueEvent, getEventReservations,
} from "../../../../utils/api/venueEventsAPI";
import type { Reservation } from "../../../../utils/api/reservationsAPI";
import { createReservation, updateReservation } from "../../../../utils/api/reservationsAPI";
import {
  exportEventReservationsPDF,
  printEventReservations,
} from "../../../../utils/helpers/exportUtils";
import AddVenueEventModal from "../AddVenueEventModal/AddVenueEventModal";
import VenueEventDetail from "../VenueEventDetail/VenueEventDetail";
import AddEventReservationModal from "../AddEventReservationModal/AddEventReservationModal";
import "./VenueEventsSection.scss";

const { Title, Text } = Typography;

const EVENT_TYPE_LABELS: Record<string, string> = {
  concert: "Concert", standup: "Stand-up", band: "Live Band",
  private: "Private Event", sports: "Sports", other: "Other",
};

const STATUS_TAG: Record<string, { color: string; label: string }> = {
  draft:     { color: "default", label: "Draft" },
  published: { color: "green",   label: "Published" },
  cancelled: { color: "red",     label: "Cancelled" },
  completed: { color: "blue",    label: "Completed" },
  archived:  { color: "default", label: "Archived" },
};

type Props = {
  clientId: string;
  canCreate?: boolean;
  canUpdate?: boolean;
  canUpdateName?: boolean;
  canUpdateCapacity?: boolean;
  canUpdateTables?: boolean;
  canDelete?: boolean;
  canAddReservation?: boolean;
};

const VenueEventsSection: React.FC<Props> = ({ clientId, canCreate, canUpdate, canUpdateName, canUpdateCapacity, canUpdateTables, canDelete, canAddReservation }) => {
  const { t } = useTranslation();
  const hasAnyUpdatePermission = canUpdate || canUpdateName || canUpdateCapacity || canUpdateTables;
  const isRestrictedEdit = !canUpdate && !!(canUpdateName || canUpdateCapacity || canUpdateTables);
  const [events, setEvents] = useState<VenueEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const [showArchived, setShowArchived] = useState(false);
  const [showExpired, setShowExpired] = useState(false);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editData, setEditData] = useState<VenueEvent | null>(null);
  const [detailEvent, setDetailEvent] = useState<VenueEvent | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [addResModal, setAddResModal] = useState<VenueEvent | null>(null);
  const [editResData, setEditResData] = useState<Reservation | null>(null);
  const [preTableNumber, setPreTableNumber] = useState<number | null>(null);
  const [takenTableNumbers, setTakenTableNumbers] = useState<number[]>([]);
  const [savingRes, setSavingRes] = useState(false);
  const [drawerRefreshKey, setDrawerRefreshKey] = useState(0);
  const [exportingId, setExportingId] = useState<string | null>(null);

  const handleCardExport = async (event: VenueEvent, type: "pdf" | "print", e: React.MouseEvent) => {
    e.stopPropagation();
    setExportingId(event._id);
    try {
      const res = await getEventReservations(event._id);
      if (type === "pdf") exportEventReservationsPDF(event, res.data);
      else printEventReservations(event, res.data);
    } catch {
      message.error(t("Failed to load reservations"));
    } finally {
      setExportingId(null);
    }
  };

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await getVenueEvents(clientId, params);
      setEvents(res.data);
      // Keep detailEvent in sync so stats refresh without reopening drawer
      setDetailEvent((prev) => {
        if (!prev) return prev;
        return res.data.find((e) => e._id === prev._id) ?? prev;
      });
    } catch {
      message.error(t("Failed to load events"));
    } finally {
      setLoading(false);
    }
  }, [clientId, statusFilter]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const handleSave = async (values: Partial<VenueEvent>) => {
    setSaving(true);
    try {
      if (editData) {
        await updateVenueEvent(editData._id, values);
        message.success(t("Event updated"));
      } else {
        await createVenueEvent(clientId, values);
        message.success(t("Event created"));
      }
      setAddModalOpen(false);
      setEditData(null);
      fetchEvents();
    } catch (err: any) {
      message.error(err?.response?.data?.error || t("Failed to save event"));
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteVenueEvent(id);
      message.success(t("Event deleted"));
      fetchEvents();
    } catch {
      message.error(t("Failed to delete event"));
    }
  };

  const handleArchive = async (id: string) => {
    try {
      await archiveVenueEvent(id);
      message.success(t("Event archived"));
      fetchEvents();
    } catch {
      message.error(t("Failed to archive event"));
    }
  };

  const handleDuplicate = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await duplicateVenueEvent(id);
      message.success(t("Event duplicated"));
      fetchEvents();
    } catch {
      message.error(t("Failed to duplicate event"));
    }
  };

  const handleSaveReservation = async (values: any) => {
    const eventForModal = addResModal || (editResData ? events.find((e) => e._id === editResData.eventId) || null : null);
    setSavingRes(true);
    try {
      if (editResData) {
        await updateReservation(editResData._id, values);
        message.success(t("Reservation updated"));
        setEditResData(null);
      } else if (addResModal) {
        await createReservation(clientId, { ...values, eventId: addResModal._id });
        message.success(t("Reservation added"));
        setAddResModal(null);
      }
      setDrawerRefreshKey((k) => k + 1);
      fetchEvents();
    } catch (err: any) {
      message.error(err?.response?.data?.error || t("Failed to save reservation"));
      throw err;
    } finally {
      setSavingRes(false);
    }
  };

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const fiveDaysAgo = new Date(today.getTime() - 5 * 24 * 60 * 60 * 1000);

  const displayedEvents = events.filter((event) => {
    const isArchived = event.status === "archived";
    const isExpiredPast = !isArchived && new Date(event.date) < fiveDaysAgo;
    if (isArchived && !showArchived) return false;
    if (isExpiredPast && !showExpired) return false;
    return true;
  });

  const publicUrl = `${window.location.origin}/events/public?clientId=${clientId}`;

  const copyPublicUrl = () => {
    navigator.clipboard.writeText(publicUrl);
    message.success(t("Public events link copied!"));
  };

  const copyEventUrl = (eventId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `${window.location.origin}/events/public?clientId=${clientId}&eventId=${eventId}`;
    navigator.clipboard.writeText(url);
    message.success(t("Event link copied!"));
  };

  return (
    <div className="venue-events-section">
      <div className="ves-toolbar">
        <div className="ves-toolbar__left">
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 160 }}
            size="large"
          >
            <Select.Option value="all">{t("All Statuses")}</Select.Option>
            <Select.Option value="published">{t("Published")}</Select.Option>
            <Select.Option value="draft">{t("Draft")}</Select.Option>
            <Select.Option value="completed">{t("Completed")}</Select.Option>
            <Select.Option value="cancelled">{t("Cancelled")}</Select.Option>
          </Select>

          <div className="ves-toolbar__checks">
            <Checkbox checked={showExpired} onChange={(e) => setShowExpired(e.target.checked)}>
              {t("Show expired")}
            </Checkbox>
            <Checkbox checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)}>
              {t("Show archived")}
            </Checkbox>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <Tooltip title={t("Copy public events link")}>
            <Button icon={<LinkOutlined />} size="large" onClick={copyPublicUrl}>
              {t("Public Link")}
            </Button>
          </Tooltip>
          {canCreate && (
            <Button
              type="primary" icon={<PlusOutlined />} size="large"
              onClick={() => { setEditData(null); setAddModalOpen(true); }}
            >
              {t("New Event")}
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="ves-loading"><Spin size="large" /></div>
      ) : displayedEvents.length === 0 ? (
        <Empty description={t("No events found")} style={{ marginTop: 60 }}>
          {canCreate && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalOpen(true)}>
              {t("Create First Event")}
            </Button>
          )}
        </Empty>
      ) : (
        <Row gutter={[16, 16]} className="ves-grid">
          {displayedEvents.map((event) => {
            const fillPct = Math.round((event.reservedCount / event.totalCapacity) * 100);
            const isFull = event.reservedCount >= event.totalCapacity;
            const st = STATUS_TAG[event.status] || { color: "default", label: event.status };
            const isArchived = event.status === "archived";
            const eventDate = new Date(event.date);
            const isExpiredPast = !isArchived && eventDate < fiveDaysAgo;
            const isPast = !isArchived && !isExpiredPast && eventDate < today;

            const cardClass = [
              "ves-card",
              event.status === "cancelled" ? "ves-card--cancelled" : "",
              isArchived ? "ves-card--archived" : "",
              (isPast || isExpiredPast) && !isArchived ? "ves-card--past" : "",
            ].filter(Boolean).join(" ");

            return (
              <Col xs={24} sm={12} lg={8} key={event._id}>
                <div
                  className={cardClass}
                  onClick={() => { setDetailEvent(event); setDetailOpen(true); }}
                >
                  <div className="ves-card__header">
                    <div>
                      <div className="ves-card__title">
                        {event.isLocked && <LockOutlined style={{ marginRight: 6, color: "#d97706" }} />}
                        {event.title}
                      </div>
                      <div className="ves-card__type">{t(EVENT_TYPE_LABELS[event.type] || event.type)}</div>
                    </div>
                    <div className="ves-card__header-right">
                      <Tag color={st.color} className="ves-card__status-tag">{t(st.label)}</Tag>
                      <Tooltip title={t("Print")}>
                        <Button
                          className="ves-card__export-btn"
                          size="small"
                          type="text"
                          icon={<PrinterOutlined />}
                          loading={exportingId === event._id}
                          onClick={(e) => handleCardExport(event, "print", e)}
                        />
                      </Tooltip>
                      <Tooltip title={t("Export PDF")}>
                        <Button
                          className="ves-card__export-btn"
                          size="small"
                          type="text"
                          icon={<FilePdfOutlined />}
                          loading={exportingId === event._id}
                          onClick={(e) => handleCardExport(event, "pdf", e)}
                        />
                      </Tooltip>
                    </div>
                  </div>

                  <div className="ves-card__meta">
                    <span><CalendarOutlined /> {dayjs(event.date).format("DD MMM YYYY")}</span>
                    <span><ClockCircleOutlined /> {event.time}{event.endTime ? ` – ${event.endTime}` : ""}</span>
                    {event.isLocked && event.lockedPrice != null ? (
                      <span>
                        <DollarOutlined /> {event.lockedPrice} {event.currency}
                        {" · "}{event.lockedPriceType === "per_person" ? t("per person") : t("flat")}
                      </span>
                    ) : !event.isLocked && event.ticketPrice > 0 ? (
                      <span><DollarOutlined /> {event.ticketPrice} {event.currency} {t("per ticket")}</span>
                    ) : null}
                  </div>

                  {(event.responsibleUserId || event.kitchenUserId) && (
                    <div className="ves-card__staff">
                      {event.responsibleUserId && typeof event.responsibleUserId === "object" && (
                        <div className="ves-card__staff-item ves-card__staff-item--event">
                          <span className="ves-card__staff-badge"><UserOutlined /></span>
                          <div>
                            <div className="ves-card__staff-role">{t("Responsible Person")}</div>
                            <div className="ves-card__staff-name">{(event.responsibleUserId as any).firstName} {(event.responsibleUserId as any).lastName}</div>
                          </div>
                        </div>
                      )}
                      {event.kitchenUserId && typeof event.kitchenUserId === "object" && (
                        <div className="ves-card__staff-item ves-card__staff-item--kitchen">
                          <span className="ves-card__staff-badge"><CoffeeOutlined /></span>
                          <div>
                            <div className="ves-card__staff-role">{t("Kitchen Responsible")}</div>
                            <div className="ves-card__staff-name">{(event.kitchenUserId as any).firstName} {(event.kitchenUserId as any).lastName}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="ves-card__capacity">
                    <div className="ves-card__capacity-label">
                      <span><TeamOutlined /> {event.reservedCount} / {event.totalCapacity}</span>
                      {isFull && <Tag color="red" style={{ fontSize: 11 }}>{t("Sold Out")}</Tag>}
                    </div>
                    <Progress
                      percent={fillPct}
                      showInfo={false}
                      size="small"
                      strokeColor={isFull ? "#ef4444" : fillPct >= 80 ? "#f59e0b" : "#10b981"}
                    />
                  </div>

                  <div className="ves-card__actions" onClick={(e) => e.stopPropagation()}>
                    {hasAnyUpdatePermission && !isArchived && (
                      <Button
                        size="small" icon={<EditOutlined />}
                        onClick={() => { setEditData(event); setAddModalOpen(true); }}
                      >
                        {t("Edit")}
                      </Button>
                    )}
                    {event.status === "published" && (
                      <Tooltip title={t("Copy link for this event")}>
                        <Button
                          size="small" icon={<LinkOutlined />}
                          onClick={(e) => copyEventUrl(event._id, e)}
                        />
                      </Tooltip>
                    )}
                    {canAddReservation && !isFull && event.status === "published" && !event.isLocked && (
                      <Button
                        size="small" type="primary" icon={<PlusOutlined />}
                        onClick={() => setAddResModal(event)}
                      >
                        {t("Add Reservation")}
                      </Button>
                    )}
                    <Tooltip title={t("Duplicate event")}>
                      <Button
                        size="small" icon={<CopyOutlined />}
                        onClick={(e) => handleDuplicate(event._id, e)}
                      />
                    </Tooltip>
                    {canUpdate && !isArchived && (
                      <Popconfirm
                        title={t("Archive this event?")}
                        description={t("Archived events will no longer be visible in the active list.")}
                        onConfirm={() => handleArchive(event._id)}
                        okText={t("Archive")} cancelText={t("Cancel")}
                      >
                        <Tooltip title={t("Archive event")}>
                          <Button size="small" icon={<InboxOutlined />} />
                        </Tooltip>
                      </Popconfirm>
                    )}
                    {canDelete && (
                      <Popconfirm
                        title={t("Delete this event?")}
                        onConfirm={() => handleDelete(event._id)}
                        okText={t("Delete")} okButtonProps={{ danger: true }} cancelText={t("Cancel")}
                      >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    )}
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>
      )}

      <AddVenueEventModal
        open={addModalOpen}
        onClose={() => { setAddModalOpen(false); setEditData(null); }}
        onSave={handleSave}
        editData={editData}
        loading={saving}
        restrictedEdit={isRestrictedEdit}
        canEditName={canUpdate || !!canUpdateName}
        canEditCapacity={canUpdate || !!canUpdateCapacity}
        canEditTables={canUpdate || !!canUpdateTables}
      />

      <VenueEventDetail
        event={detailEvent}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        onEdit={hasAnyUpdatePermission ? (e) => { setEditData(e); setAddModalOpen(true); } : undefined}
        onDelete={canDelete ? handleDelete : undefined}
        onAddReservation={canAddReservation ? (e, tableNum, reservations) => {
          setAddResModal(e);
          setPreTableNumber(tableNum || null);
          setTakenTableNumbers(
            (reservations || [])
              .filter((r) => r.eventTableNumber && !["cancelled", "no-show"].includes(r.status))
              .map((r) => r.eventTableNumber!)
          );
        } : undefined}
        onEditReservation={canUpdate ? (r) => setEditResData(r) : undefined}
        refreshKey={drawerRefreshKey}
      />

      {(addResModal || editResData) && (
        <AddEventReservationModal
          open={!!(addResModal || editResData)}
          event={addResModal || events.find((e) => e._id === editResData?.eventId)!}
          editData={editResData}
          onClose={() => {
            setAddResModal(null);
            setEditResData(null);
            setPreTableNumber(null);
            setTakenTableNumbers([]);
          }}
          onSave={handleSaveReservation}
          loading={savingRes}
          initialTableNumber={preTableNumber}
          takenTableNumbers={takenTableNumbers}
        />
      )}
    </div>
  );
};

export default VenueEventsSection;
